import type * as React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import {
  formatUsdWithSpaceAfterSymbol,
  useAnimatedUsdString
} from "components/v3/sharedComponents/AnimatedUsdAmount";
import { COLORS } from "components/v3/Theme/colors";
import { GIFT_UNIT_PRICE_USD } from "constants/membershipPriceConstants";
import { GENOMIC_LIFE_COMPLETE_MEMBER_INTRO } from "constants/genomicLifeCompleteProgramCopy";
import { PATHS } from "constants/pathConstants";

export type GiftRecipientsCheckoutSidebarProps = {
  showPricingSummary?: boolean;
  planCount?: number;
};

const GiftSidebarPricingSummary: React.FC<{ planCount: number }> = ({
  planCount
}) => {
  const count = Math.max(1, planCount);
  const unitLabel = formatUsdWithSpaceAfterSymbol(GIFT_UNIT_PRICE_USD);
  const totalLabel = useAnimatedUsdString(count * GIFT_UNIT_PRICE_USD);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        component="h2"
        sx={{ m: 0, mb: 0.75 }}
        variant="body_strong_black"
      >
        Genomic Life Complete Plan:
      </Typography>
      <Typography
        sx={{ color: COLORS.DARK_GRAY_2, m: 0, mb: 1 }}
        variant="body"
      >
        Your additional cost
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          lineHeight: 1.3,
          m: 0,
          mb: 0.5
        }}
        variant="body_strong_dark_gray"
      >
        {count} x {unitLabel}
      </Typography>
      <Typography
        sx={{
          color: COLORS.ACCENT2,
          fontVariantNumeric: "tabular-nums",
          m: 0
        }}
        variant="body_strong_lg_accent2"
      >
        {totalLabel}
      </Typography>
    </Box>
  );
};

const GiftRecipientsCheckoutSidebar: React.FC<
  GiftRecipientsCheckoutSidebarProps
> = ({ showPricingSummary = false, planCount = 1 }) => (
  <Box
    sx={{
      alignSelf: { lg: "flex-start", xs: "stretch" },
      bgcolor: COLORS.BACKGROUND_BLUE,
      borderRadius: "12px",
      boxSizing: "border-box",
      flexShrink: 0,
      p: 2.5,
      width: { lg: 360, xs: "100%" }
    }}
  >
    {showPricingSummary ? (
      <GiftSidebarPricingSummary planCount={planCount} />
    ) : (
      <Typography component="h2" sx={{ mb: 2 }} variant="body_strong_lg_black">
        Genomic Life Complete Plan
      </Typography>
    )}

    <Typography
      sx={{ color: COLORS.BLACK, lineHeight: 1.65, mb: 3 }}
      variant="body"
    >
      {GENOMIC_LIFE_COMPLETE_MEMBER_INTRO}
    </Typography>
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        lineHeight: 0.45,
        m: 0,
        mb: 4,
        p: 0,
        typography: "body_strong_dark_gray"
      }}
    >
      <Box component="li" sx={{ mb: 0.5 }}>
        → DNA Screenings
      </Box>
      <Box component="li" sx={{ mb: 0.5 }}>
        → Personalized Health Guidance
      </Box>
      <Box component="li">→ Cancer Navigation</Box>
    </Box>

    <Divider
      sx={{
        borderColor: alpha(COLORS.DARK_GRAY, 0.18),
        mb: 4,
        mt: 0
      }}
    />

    <Typography component="p" sx={{ m: 0, mb: 2 }} variant="body_dark_gray">
      Please note that Genomic Life can only serve people 18 years and older who
      are located in the United States. Age verification is required upon sign
      up.
    </Typography>

    <Typography
      component="p"
      sx={{ lineHeight: 1.65, mb: 2 }}
      variant="body_dark_gray"
    >
      → First-year membership included
      <br />
      → Gift recipients have the opportunity to renew after 1 year
      <br />→ Gift Memberships are subject to our refund and cancellation policy
    </Typography>
    <Typography component="p" sx={{ m: 0 }} variant="body_dark_gray">
      Please see our{" "}
      <Box
        component={RouterLink}
        rel="noopener noreferrer"
        target="_blank"
        to={PATHS.ACCOUNT_MANAGEMENT_TERMS_AND_CONDITIONS}
        sx={{
          color: "inherit",
          textDecoration: "underline",
          textUnderlineOffset: "2px"
        }}
      >
        Terms of Use
      </Box>{" "}
      and{" "}
      <Box
        component={RouterLink}
        rel="noopener noreferrer"
        target="_blank"
        to={PATHS.ACCOUNT_MANAGEMENT_PRIVACY_POLICY}
        sx={{
          color: "inherit",
          textDecoration: "underline",
          textUnderlineOffset: "2px"
        }}
      >
        Privacy Policy
      </Box>{" "}
      for details.
    </Typography>
  </Box>
);

export default GiftRecipientsCheckoutSidebar;
